import React from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { EChartOption } from 'echarts';
import { get } from 'lodash';
import { ChartEvent } from '@components/common/Charts/BaseChart/BaseChart.typed';
import CustomChartTooltip from '@components/session-timeline/components/CustomChartTooltip';
import useEventOption from '@components/session-timeline/hooks/useEventOption';
import useLinerOption from '@components/session-timeline/hooks/useLinerOption';
import useStateOption from '@components/session-timeline/hooks/useStateOption';
import { onExpandEvent, highlightMomentByEvent, removeHighlightByEvent } from '@store/timeline/ui/actions';
import { TIMELINE_DIMENSION } from '@components/session-timeline/utils/constants';
import useClicked from '@components/session-timeline/hooks/useClicked';
import { CHART_TYPE } from './enums';
import { Wrapper, StyledBaseChart } from './styled';
import { TimelineChartProps } from './types';
import 'echarts/lib/chart/custom';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/grid';
import 'echarts/lib/component/graphic';
import 'echarts/lib/component/singleAxis';

const clickableEvent = [
  TIMELINE_DIMENSION.Error,
  TIMELINE_DIMENSION.Bitrate,
  TIMELINE_DIMENSION.CustomEvent
] as string[];

const GetOption = (event: string, type: CHART_TYPE, dateRange: number[]): EChartOption => {
  if (type === CHART_TYPE.LINE) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useLinerOption(event, dateRange);
  }
  if (type === CHART_TYPE.STATE) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useStateOption(event, dateRange);
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useEventOption(event, dateRange);
}

const TimelineChart = ({ type, event, highlightMoment, dateRange }: TimelineChartProps) => {
  const option = GetOption(event, type, dateRange);
  const [chartDOMRef, x, text] = useClicked(highlightMoment, event, dateRange);
  const dispatch = useDispatch();

  const handleClick = (e) => {
    const currentTime = get(e, ['value', 0], 0);
    if (!currentTime || !clickableEvent.includes(event)) {
      return;
    }
    if (Array.isArray(currentTime) && highlightMoment !== currentTime[0]) {
      dispatch(onExpandEvent(event, false));
      dispatch(highlightMomentByEvent(event, moment(currentTime[0]).valueOf()));
    } else if (!Array.isArray(currentTime) && currentTime !== highlightMoment) {
      dispatch(onExpandEvent(event, false));
      dispatch(highlightMomentByEvent(event, moment(currentTime).valueOf()));
    } else {
      dispatch(removeHighlightByEvent(event));
    }
  }

  const events: ChartEvent[] = React.useMemo(() => [
    ['click', handleClick],
  ], [dispatch, event, highlightMoment]);

  return (
    <Wrapper>
      <StyledBaseChart option={option} ref={chartDOMRef} events={events} renderer="svg"/>
      {highlightMoment && (
        <CustomChartTooltip text={text} left={x} />
      )}
    </Wrapper>
  );
};

export default TimelineChart;
